import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '@/services/api';
import { toast } from '@/hooks/use-toast';

interface OrganizationBranding {
  primaryColor?: string;
  secondaryColor?: string;
  logo?: string;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  branding?: OrganizationBranding;
  createdAt?: string;
  updatedAt?: string;
}

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  organization?: Organization | null;
  avatar?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface Session {
  createdAt: string;
  lastActivity: string;
  ipAddress?: string;
  userAgent?: string;
  current: boolean;
}

interface AuthContextType {
  user: User | null;
  organization: Organization | null;
  sessions: Session[];
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (email: string, password: string, name: string, organizationName?: string) => Promise<void>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
  refreshUser: () => Promise<void>;
  switchOrganization: (organizationId: string) => Promise<void>;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const response = await authAPI.getCurrentUser();
      if (response.user) {
        setUser(response.user);
        setOrganization(response.user.organization || null);
      }
    } catch (error) {
      // Silently fail - user might not be logged in
      console.debug('No active session');
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      const response = await authAPI.getSessions();
      setSessions(response.sessions || []);
    } catch (error) {
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    // Check for existing session on mount
    const checkAuth = async () => {
      setIsLoading(true);
      await refreshUser();
      if (user) {
        await fetchSessions();
      }
      setIsLoading(false);
    };

    checkAuth();

    // Listen for logout events from API interceptor
    const handleLogout = () => {
      setUser(null);
      setOrganization(null);
      setSessions([]);
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, []);

  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      const response = await authAPI.login(email, password, rememberMe);
      
      if (response.user) {
        setUser(response.user);
        setOrganization(response.user.organization || null);
        
        // Fetch sessions after login
        await fetchSessions();
        
        toast({
          title: 'Welcome back!',
          description: `Logged in as ${response.user.name}`,
        });
      }
    } catch (error) {
      const axiosError = error as { response?: { data?: { error?: string } } };
      const message = axiosError.response?.data?.error || 'Login failed. Please try again.';
      toast({
        title: 'Login Failed',
        description: message,
        variant: 'destructive',
      });
      throw error;
    }
  };

  const register = async (email: string, password: string, name: string, organizationName?: string) => {
    try {
      const response = await authAPI.register(email, password, name, organizationName);

      if (response.user) {
        setUser(response.user);
        setOrganization(response.user.organization || null);

        toast({
          title: 'Account created!',
          description: 'Welcome to SixSight',
        });
      }
    } catch (error) {
      const axiosError = error as { response?: { data?: { error?: string } } };
      const message = axiosError.response?.data?.error || 'Registration failed. Please try again.';
      toast({
        title: 'Registration Failed',
        description: message,
        variant: 'destructive',
      });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
      setUser(null);
      setOrganization(null);
      setSessions([]);
      
      toast({
        title: 'Logged out',
        description: 'You have been successfully logged out.',
      });
    } catch (error) {
      console.error('Logout error:', error);
      // Clear state even if logout fails
      setUser(null);
      setOrganization(null);
      setSessions([]);
    }
  };

  const logoutAll = async () => {
    try {
      await authAPI.logoutAll();
      setUser(null);
      setOrganization(null);
      setSessions([]);
      
      toast({
        title: 'All sessions ended',
        description: 'You have been logged out from all devices.',
      });
    } catch (error) {
      console.error('Logout all error:', error);
      toast({
        title: 'Error',
        description: 'Failed to logout from all sessions.',
        variant: 'destructive',
      });
    }
  };

  const switchOrganization = async (organizationId: string) => {
    try {
      const response = await authAPI.switchOrganization(organizationId);
      
      if (response.organization) {
        setOrganization(response.organization);
        
        toast({
          title: 'Organization switched',
          description: `Now working in ${response.organization.name}`,
        });
        
        // Refresh user data to ensure consistency
        await refreshUser();
      }
    } catch (error) {
      const axiosError = error as { response?: { data?: { error?: string } } };
      const message = axiosError.response?.data?.error || 'Failed to switch organization.';
      toast({
        title: 'Switch Failed',
        description: message,
        variant: 'destructive',
      });
      throw error;
    }
  };

  const value = {
    user,
    organization,
    sessions,
    login,
    register,
    logout,
    logoutAll,
    refreshUser,
    switchOrganization,
    isLoading,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}